import React, { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTaskFinderContext } from "./context/TaskFinderContext";
import { TaskRenderer } from "@/modern/components/task-renderer";
import { Task } from "@/bryntum-lib/models/Task";
import { getFontSize } from "@/react/context/bryntumContext";

export const TaskFinderContent = () => {
  const { filteredTasks, selectedTasks, handleTaskSelect, handleTaskClick, handleTaskDblClick, handleSubmit, canInsert } = useTaskFinderContext();

  const containerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredTasks.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3, /* 2.2 Rem */
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const isTaskSelected = (task: Task) => {
    return selectedTasks.some((t) => t.id === task.id);
  };

  const handleTaskItemClick = (task: Task, event: React.MouseEvent) => {
    if (event.altKey && canInsert) {
      // Alt+Click triggers submit if canInsert is true
      handleTaskSelect(task);
      setTimeout(() => handleSubmit(), 0);
    } else if (canInsert) {
      // Regular click selects/deselects in insert mode
      handleTaskSelect(task);
    } else {
      // In non-insert mode, clicking opens the task
      handleTaskClick(task);
    }
  };

  const handleTaskItemDblClick = (task: Task) => {
    if (!canInsert) {
      handleTaskDblClick(task);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background user-select-none px-4 py-2">
        {filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="size-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-sm">No tasks found</p>
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{
              height: `${totalSize}px`
            }}
          >
            {virtualItems.map((virtualRow) => {
              const task = filteredTasks[virtualRow.index];
              const isSelected = isTaskSelected(task);
              return (
                <div
                  key={task.id}
                  className={`swivu-task-finder-drag-handler absolute left-0 top-0 cursor-pointer rounded transition-all w-full px-0 py-1 truncate ${isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:bg-accent"}`}
                  data-swivu-task-id={task.id}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                  onClick={(e) => handleTaskItemClick(task, e)}
                  onDoubleClick={() => handleTaskItemDblClick(task)}
                >
                  <TaskRenderer record={task} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
